/**
 * <TutorSoundModeToggle> — Dialogue ou Concentré, incrusté en bas à droite de la vidéo d'un élève
 * (molecule).
 *
 * Weekly du 03/11 (W5) : la bascule quitte le dessous de la vidéo, où elle prenait toute la largeur de la
 * carte, pour un seul bouton rond posé dans la visio. Le mode se lit d'un coup d'œil, par le glyphe et
 * l'aplat de `<TutorSoundModeSwitch>` (le micro sur la tinte de progression pour Dialogue, le son coupé
 * sur le violet du live pour Concentré) ; un toucher passe à l'autre mode. La bascule qui part se voit :
 * le bouton tourne, sans répondre, jusqu'à ce que l'écran pose le nouveau mode.
 *
 * L'élève parti : le bouton se lit, estompé, et ne se touche pas. Contrôlé : le mode arrive en prop.
 */
import React from "react"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { slopTo } from "../../utils/touchTarget"
import { SOUND_MODE_LABELS } from "../SoundModeBadge"
import { SOUND_MODE_FACE } from "../TutorSoundModeSwitch/TutorSoundModeSwitch"

export type TutorSoundModeToggleProps = {
    mode: UISoundMode
    onChange: (mode: UISoundMode) => void
    /** L'élève est parti : le bouton se lit, ne se touche pas. Défaut `false`. */
    disabled?: boolean
    /** La bascule part : le bouton tourne et ne répond plus. Défaut `false`. */
    pending?: boolean
    /** Le prénom de l'élève, pour le lecteur d'écran. */
    studentName: string
    /** Le diamètre. Défaut 32 : une vignette de la pellicule ; la mosaïque en prend un plus grand. */
    size?: number
    testID?: string
}

const OTHER: Record<UISoundMode, UISoundMode> = { dialogue: "focused", focused: "dialogue" }

/** Ce que dit le bouton : le mode posé, puis ce que fait le toucher. */
export function tutorSoundModeToggleLabel(mode: UISoundMode, studentName: string): string {
    return `${studentName} en ${SOUND_MODE_LABELS[mode]}. Passer en ${SOUND_MODE_LABELS[OTHER[mode]]}`
}

export function TutorSoundModeToggle(props: TutorSoundModeToggleProps) {
    const { mode, onChange, disabled = false, pending = false, studentName, size = 32, testID } = props
    const face = SOUND_MODE_FACE[mode]
    return (
        <IconButton
            iconName={face.icon}
            color={face.fill}
            iconColor={face.ink}
            size={size}
            iconSize={Math.round(size * 0.45)}
            disabled={disabled}
            loading={pending}
            // Un bouton de 32 px dans la vidéo : la cible s'étend à celle d'un doigt, pas le toucher de la vidéo.
            hitSlop={slopTo(size)}
            onPress={() => onChange(OTHER[mode])}
            accessibilityLabel={tutorSoundModeToggleLabel(mode, studentName)}
            testID={testID}
        />
    )
}
